// packages/atoms/hooks/src/company.tsx
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate, useParams, type NavigateOptions } from 'react-router-dom';
import { v2 } from '@lendica/api';
import type { Membership as V2Membership, Company } from '@lendica/utils';
import type { UserCompanyStatusResponse } from '@lendica/api';

type NormalizedCompany = {
    companyId: string;
    companyName?: string;
    slug?: string;
};
type CompanyContextValue = {
    memberships: NormalizedCompany[];
    activeCompanyId: string;
    setActiveCompanyId: (id: string) => void;
    activeCompanyKey: string;
    membershipsLoading: boolean;
    isCompanyResolved: boolean;
    hasMemberships: boolean;
    refreshMemberships: (options?: { force?: boolean }) => Promise<NormalizedCompany[]>;
    getCompanyDetail: (id: string) => Company | undefined;
    getCompanyName: (id: string) => string;
    prefetchCompanyDetails: (
        ids?: string[],
        options?: { force?: boolean; skipIfHasBasics?: boolean }
    ) => Promise<void>;
    getCompanyUserStatus: (id: string) => UserCompanyStatusResponse | undefined;
    getCompanyUserStatusLoadState: (id: string) => 'success' | 'error' | undefined;
    prefetchCompanyUserStatus: (ids?: string[], options?: { force?: boolean }) => Promise<void>;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
    const [memberships, setMemberships] = useState<NormalizedCompany[] | null>(null);
    const [activeCompanyId, setActiveCompanyIdState] = useState<string>(
        () => localStorage.getItem('activeCompanyId') ?? ''
    );
    const [companyDetailsById, setCompanyDetailsById] = useState<Record<string, Company>>({});
    const [companyUserStatusById, setCompanyUserStatusById] = useState<
        Record<string, UserCompanyStatusResponse>
    >({});
    const [companyUserStatusLoadState, setCompanyUserStatusLoadState] = useState<
        Record<string, 'success' | 'error'>
    >({});
    const inFlightFetchIdsRef = useRef<Set<string>>(new Set());
    const inFlightStatusIdsRef = useRef<Set<string>>(new Set());

    const { companyKey } = useParams<{ companyKey?: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const fetchMemberships = useCallback(async () => {
        const res = await v2.listMemberships();
        const raw = (res ?? []) as V2Membership[] | any[];
        return normalizeMemberships(raw);
    }, []);

    const refreshMemberships = useCallback(
        async (options?: { force?: boolean }) => {
            const force = options?.force ?? false;
            if (!force && memberships !== null) return memberships;
            try {
                const list = await fetchMemberships();
                setMemberships(list);
                return list;
            } catch (error) {
                if (memberships === null) {
                    setMemberships([]);
                }
                throw error;
            }
        },
        [fetchMemberships, memberships]
    );

    useEffect(() => {
        if (memberships !== null) return;
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchMemberships();
                if (!cancelled) {
                    setMemberships(list);
                }
            } catch {
                if (!cancelled) {
                    setMemberships([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [memberships, fetchMemberships]);

    const setActiveCompanyId = (id: string) => {
        setActiveCompanyIdState(id);
        localStorage.setItem('activeCompanyId', id);
    };

    const resolvedId = useMemo(() => {
        if (memberships === null) return '';
        if (!memberships.length) return '';
        if (companyKey) {
            const hit =
                memberships.find(m => m.slug === companyKey) ??
                memberships.find(m => m.companyId === companyKey);
            if (hit) return hit.companyId;
        }
        const stored = localStorage.getItem('activeCompanyId');
        if (stored && memberships.some(m => m.companyId === stored)) return stored;
        return memberships[0]?.companyId ?? '';
    }, [companyKey, memberships]);

    useEffect(() => {
        if (memberships === null) return; // still loading memberships
        if (resolvedId && activeCompanyId !== resolvedId) setActiveCompanyId(resolvedId);

        // Normalize URL key to slug if available when a company is resolved
        if (resolvedId) {
            const canonical = memberships.find(m => m.companyId === resolvedId);
            const wantKey = canonical?.slug ?? canonical?.companyId;
            if (wantKey && companyKey && companyKey !== wantKey) {
                navigate(
                    location.pathname.replace(`/c/${companyKey}`, `/c/${wantKey}`) +
                        location.search,
                    { replace: true }
                );
            }
        }
    }, [memberships, resolvedId, activeCompanyId, companyKey, navigate, location]);

    // Prefetch helpers
    const getCompanyDetail = (id: string) => companyDetailsById[id];
    const getCompanyName = (id: string) => companyDetailsById[id]?.company_name;
    const getCompanyUserStatus = (id: string) => companyUserStatusById[id];
    const getCompanyUserStatusLoadState = (id: string) => companyUserStatusLoadState[id];

    const prefetchCompanyDetails = async (
        ids?: string[],
        options?: { force?: boolean; skipIfHasBasics?: boolean }
    ) => {
        const force = options?.force ?? false;
        const skipIfHasBasics = options?.skipIfHasBasics ?? false;
        const sourceIds = ids ?? (memberships ?? []).map(m => m.companyId);
        if (!sourceIds || sourceIds.length === 0) return;

        const toFetch = sourceIds.filter(id => {
            if (!id) return false;
            if (inFlightFetchIdsRef.current.has(id)) return false;
            if (!force && companyDetailsById[id]) {
                if (skipIfHasBasics) {
                    const detail = companyDetailsById[id];
                    if (detail?.company_name && detail?.email) return false;
                } else {
                    return false;
                }
            }
            return true;
        });

        if (toFetch.length === 0) return;

        toFetch.forEach(id => inFlightFetchIdsRef.current.add(id));
        try {
            await Promise.allSettled(
                toFetch.map(async id => {
                    try {
                        const detail = await v2.getCompanyDetails(id);
                        setCompanyDetailsById(prev => ({ ...prev, [id]: detail as Company }));
                    } catch {
                        // noop per id
                    } finally {
                        inFlightFetchIdsRef.current.delete(id);
                    }
                })
            );
        } finally {
            toFetch.forEach(id => inFlightFetchIdsRef.current.delete(id));
        }
    };

    const prefetchCompanyUserStatus = async (ids?: string[], options?: { force?: boolean }) => {
        const force = options?.force ?? false;
        const sourceIds = ids ?? (memberships ?? []).map(m => m.companyId);
        if (!sourceIds || sourceIds.length === 0) return;

        const toFetch = sourceIds.filter(id => {
            if (!id) return false;
            if (!force && companyUserStatusById[id]) return false;
            if (!force && companyUserStatusLoadState[id]) return false;
            if (inFlightStatusIdsRef.current.has(id)) return false;
            return true;
        });

        if (toFetch.length === 0) return;

        toFetch.forEach(id => inFlightStatusIdsRef.current.add(id));
        try {
            await Promise.allSettled(
                toFetch.map(async id => {
                    try {
                        const status = await v2.getUserCompanyStatus(id);
                        setCompanyUserStatusById(prev => ({ ...prev, [id]: status }));
                        setCompanyUserStatusLoadState(prev => ({ ...prev, [id]: 'success' }));
                        if (status?.company) {
                            setCompanyDetailsById(prev => {
                                const existing = prev[id] ?? ({} as Company);
                                const merged: Company = {
                                    ...existing,
                                    id: status.company.id ?? existing.id ?? id,
                                    status: (status.company.status as any) ?? existing.status ?? 0,
                                    company_name:
                                        (status.company.name as any) ?? existing.company_name ?? '',
                                    email: (status.company.email as any) ?? existing.email ?? null,
                                } as Company;
                                return { ...prev, [id]: merged };
                            });
                        }
                    } catch {
                        setCompanyUserStatusLoadState(prev => ({ ...prev, [id]: 'error' }));
                    } finally {
                        inFlightStatusIdsRef.current.delete(id);
                    }
                })
            );
        } finally {
            toFetch.forEach(id => inFlightStatusIdsRef.current.delete(id));
        }
    };

    useEffect(() => {
        if (!activeCompanyId) return;

        if (
            !companyUserStatusById[activeCompanyId] &&
            !companyUserStatusLoadState[activeCompanyId]
        ) {
            void prefetchCompanyUserStatus([activeCompanyId]);
        }

        const detail = companyDetailsById[activeCompanyId];
        if (!detail || !detail.company_name || !detail.email) {
            void prefetchCompanyDetails([activeCompanyId], { skipIfHasBasics: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCompanyId]);

    useEffect(() => {
        if (!memberships || memberships.length === 0) return;

        void prefetchCompanyUserStatus();
        void prefetchCompanyDetails(undefined, { skipIfHasBasics: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [!!memberships]);

    const activeCompanyKey = activeCompanyId
        ? memberships?.find(m => m.companyId === activeCompanyId)?.slug ?? activeCompanyId
        : companyKey ?? '';

    const membershipsLoading = memberships === null;
    const hasMemberships = !!memberships && memberships.length > 0;
    const isCompanyResolved = membershipsLoading ? false : !hasMemberships || !!activeCompanyId;

    return (
        <CompanyContext.Provider
            value={{
                memberships: memberships ?? [],
                activeCompanyId,
                setActiveCompanyId,
                activeCompanyKey,
                membershipsLoading,
                isCompanyResolved,
                hasMemberships,
                refreshMemberships,
                getCompanyDetail,
                getCompanyName,
                prefetchCompanyDetails,
                getCompanyUserStatus,
                getCompanyUserStatusLoadState,
                prefetchCompanyUserStatus,
            }}
        >
            {children}
        </CompanyContext.Provider>
    );
}

export function useCompanyContext() {
    const ctx = useContext(CompanyContext);
    if (!ctx) throw new Error('CompanyContext not available');
    return ctx;
}
export function useMemberships() {
    return useCompanyContext().memberships;
}
export function useActiveCompanyId() {
    return useCompanyContext().activeCompanyId;
}
export function useActiveCompanyKey() {
    return useCompanyContext().activeCompanyKey;
}

// Builds a company-scoped path if a company key is available
export function useCompanyPath() {
    const companyKey = useActiveCompanyKey();
    return (path: string) => (companyKey ? `/c/${companyKey}${path}` : path);
}

// Navigate helper that automatically scopes paths with the active company key
export function useCompanyNavigate() {
    const navigate = useNavigate();
    const toCompanyPath = useCompanyPath();
    return (path: string, options?: NavigateOptions) => navigate(toCompanyPath(path), options);
}

export function useCompanyUserStatus(targetCompanyId?: string) {
    const { activeCompanyId, getCompanyUserStatus, getCompanyUserStatusLoadState } =
        useCompanyContext();
    const companyId = targetCompanyId ?? activeCompanyId;
    const status = companyId ? getCompanyUserStatus(companyId) : undefined;
    const loadState = companyId ? getCompanyUserStatusLoadState(companyId) : undefined;
    const loading = !!companyId && !loadState;
    const error = loadState === 'error';

    return {
        companyId,
        status,
        loading,
        error,
    };
}

export function useActiveCompanyEmailVerified() {
    const { companyId, status, loading } = useCompanyUserStatus();
    return {
        companyId,
        emailVerified: status?.email_verified,
        loading,
    };
}

function normalizeMemberships(raw: V2Membership[] | any[]): NormalizedCompany[] {
    return (raw || [])
        .map((m: any) => {
            const companyId: string | undefined = m?.company;
            if (!companyId) return null;
            return {
                companyId,
            } as NormalizedCompany;
        })
        .filter((x): x is NormalizedCompany => !!x);
}
